import {
  ShoppingCart,
  Truck,
  Store,
  Users,
  Handshake,
  ArrowRightLeft,
  LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export type ProspectRelationship =
  | 'Potential Buyer'
  | 'Potential Supplier'
  | 'Potential Seller'
  | 'Potential Client'
  | 'Potential Partner'
  | 'Potential Trader'

interface RelationshipConfig {
  label: string
  color: string
  darkColor: string
  icon: LucideIcon
}

export const RELATIONSHIP_CONFIG: Record<ProspectRelationship, RelationshipConfig> = {
  'Potential Buyer': {
    label: 'Potential Buyer',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    darkColor: 'dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    icon: ShoppingCart,
  },
  'Potential Supplier': {
    label: 'Potential Supplier',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    darkColor: 'dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    icon: Truck,
  },
  'Potential Seller': {
    label: 'Potential Seller',
    color: 'bg-green-100 text-green-700 border-green-200',
    darkColor: 'dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
    icon: Store,
  },
  'Potential Client': {
    label: 'Potential Client',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    darkColor: 'dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
    icon: Users,
  },
  'Potential Partner': {
    label: 'Potential Partner',
    color: 'bg-gray-100 text-gray-700 border-gray-200',
    darkColor: 'dark:bg-gray-800/50 dark:text-gray-300 dark:border-gray-700',
    icon: Handshake,
  },
  'Potential Trader': {
    label: 'Potential Trader',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    darkColor: 'dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
    icon: ArrowRightLeft,
  },
}

interface ProspectRelationshipBadgeProps {
  relationship: string | null | undefined
  showIcon?: boolean
  size?: 'sm' | 'md'
  className?: string
}

export function ProspectRelationshipBadge({
  relationship,
  showIcon = true,
  size = 'md',
  className,
}: ProspectRelationshipBadgeProps) {
  // Return null if no relationship is provided
  if (!relationship) {
    return null
  }

  // Get config for the relationship, default to Partner if not found
  const config =
    RELATIONSHIP_CONFIG[relationship as ProspectRelationship] ||
    RELATIONSHIP_CONFIG['Potential Partner']
  const Icon = config.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border font-medium',
        config.color,
        config.darkColor,
        size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-xs',
        className
      )}
    >
      {showIcon && (
        <Icon className={cn(size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      )}
      {config.label}
    </span>
  )
}
